import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiHeart,
  FiPlus,
  FiStar,
  FiTruck,
} from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/main.scss";

const categoryTiles = [
  { label: "KEYBOARDS", query: "Keyboards", image: "/img/nav-prism-keyboard.png" },
  { label: "MICE", query: "Mice", image: "/img/product-17.webp" },
  { label: "MOUSEPADS", query: "Mousepads", image: "/img/product-25.webp" },
  { label: "KEYCAPS", query: "Keycaps", image: "/img/product-22.webp" },
];

const testimonials = [
  {
    title: "BUILT FOR COMPETITION",
    quote:
      "The response feels immediate, but it is the balance and sound that keep this board on my desk every day.",
    author: "MIRA K. — COMPETITIVE PLAYER",
  },
  {
    title: "A REAL CENTERPIECE",
    quote:
      "It performs like serious hardware and still looks considered from every angle. That combination is rare.",
    author: "JON B. — CREATIVE DIRECTOR",
  },
  {
    title: "READY OUT OF THE BOX",
    quote:
      "No immediate modifications needed. The switches, stabilizers, and acoustic tuning are already dialed in.",
    author: "ALIX R. — KEYBOARD ENTHUSIAST",
  },
];

function ProductCard({ product }) {
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);
  const discount = product.compareAtPrice
    ? Math.round((1 - product.price / product.compareAtPrice) * 100)
    : 0;

  return (
    <article className="home-product-card">
      <div className="home-product-card__media">
        <Link to={`/shopDetails/${product.id}`} aria-label={`View ${product.name}`}>
          <img src={product.images[0]} alt={product.name} loading="lazy" />
          <img className="home-product-card__hover" src={product.images[1]} alt="" loading="lazy" />
        </Link>
        {(product.badge || discount > 0) && (
          <span className="home-product-card__badge">
            {product.badge || `${discount}% OFF`}
          </span>
        )}
        <button
          type="button"
          className="home-product-card__wish"
          onClick={() => toggleWishlist(product.id)}
          aria-label={`${isWishlisted(product.id) ? "Remove" : "Add"} ${product.name} ${isWishlisted(product.id) ? "from" : "to"} wishlist`}
          aria-pressed={isWishlisted(product.id)}
        >
          <FiHeart fill={isWishlisted(product.id) ? "currentColor" : "none"} />
        </button>
        <button
          type="button"
          className="home-product-card__quick"
          onClick={() => {
            addToCart(product.id, 1, product.size[0], product.colors[0]);
            window.dispatchEvent(new CustomEvent("hushcider:open-cart"));
          }}
          aria-label={`Quick add ${product.name}`}
        >
          <FiPlus /> QUICK ADD
        </button>
      </div>
      <div className="home-product-card__body">
        <div className="home-product-card__rating" aria-label={`${product.rating} out of 5 stars`}>
          <FiStar fill="currentColor" /> {product.rating}
          <span>({product.reviews})</span>
        </div>
        <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
        <div className="home-product-card__price">
          <strong>{formatPrice(product.price)}</strong>
          {product.compareAtPrice && <del>{formatPrice(product.compareAtPrice)}</del>}
        </div>
      </div>
    </article>
  );
}

function Main() {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const dedicatedImageProductIds = new Set([3, 5, 11, 13, 17, 22, 25]);
  const featured = productData
    .filter((product) => product.featured && !dedicatedImageProductIds.has(product.id))
    .slice(0, 8);
  const bestSellers = [...productData]
    .filter((product) => !dedicatedImageProductIds.has(product.id))
    .sort((a, b) => b.reviews - a.reviews)
    .slice(0, 8);

  useEffect(() => {
    document.title = `${siteConfig.wordmark} | Premium Gaming Keyboards`;
  }, []);

  return (
    <main className="home-page">
      <section className={`home-hero ${heroLoaded ? "is-loaded" : ""}`}>
        <picture>
          <source media="(max-width: 680px)" srcSet="/img/hero-keyboard-mobile.webp" />
          <img
            src="/img/hero-keyboard.webp"
            alt="Original graphite and orange mechanical gaming keyboard"
            onLoad={() => setHeroLoaded(true)}
          />
        </picture>
        <div className="home-hero__loading" aria-hidden={heroLoaded} />
        <div className="home-hero__scrim" />
        <div className="home-hero__content">
          <p className="eyebrow">ENGINEERED FOR SOUND + PERFORMANCE</p>
          <h1>PREMIUM<br />GAMING KEYBOARDS</h1>
          <p>Purpose-built hardware. Graphic design with a point of view.</p>
          <div className="home-hero__actions">
            <Link className="site-button site-button--light" to="/shop?category=Keyboards">
              SHOP KEYBOARDS
            </Link>
            <Link className="site-button" to="/shop?sort=featured">
              BEST SELLERS
            </Link>
          </div>
        </div>
      </section>

      <section className="home-editorial" aria-label="Featured collections">
        <Link className="home-editorial__feature" to="/shop?q=Prism">
          <img src="/img/product-05.webp" alt="Prism Polar Ice keyboard" />
          <div>
            <span>01 / NEW TECHNOLOGY</span>
            <h2>PRISM 65HE</h2>
            <p>Rapid trigger. Adjustable actuation. A translucent new form.</p>
            <strong>EXPLORE THE DROP <FiArrowRight /></strong>
          </div>
        </Link>
        <Link className="home-editorial__feature home-editorial__feature--dark" to="/shop?q=Crest">
          <img src="/img/product-13.webp" alt="Crest Bronze Night keyboard" />
          <div>
            <span>02 / WEIGHTED ALUMINUM</span>
            <h2>CREST SERIES</h2>
            <p>Built substantial. Tuned quiet. Finished to be seen.</p>
            <strong>SHOP CREST <FiArrowRight /></strong>
          </div>
        </Link>
        <Link className="home-editorial__feature home-editorial__feature--orange" to="/shop?q=Signal">
          <img src="/img/product-03.webp" alt="Signal Orange keyboard" />
          <div>
            <span>03 / GRAPHIC COLOR</span>
            <h2>SIGNAL ORANGE</h2>
            <p>A decisive palette for work and play.</p>
            <strong>SHOP THE SERIES <FiArrowRight /></strong>
          </div>
        </Link>
      </section>

      <section className="proof-strip" aria-label="Store highlights">
        <div><strong>100,000+</strong><span>SETUPS ELEVATED</span></div>
        <div><strong>4.8 / 5</strong><span>AVERAGE RATING</span></div>
        <div><FiTruck /><span>TRACKED SHIPPING {formatPrice(siteConfig.commerce.standardShipping)}</span></div>
        <div><strong>30 DAYS</strong><span>TO MAKE IT YOURS</span></div>
      </section>

      <section className="home-products">
        <div className="home-section-head">
          <div>
            <p className="eyebrow">LIMITED COLOR. SERIOUS HARDWARE.</p>
            <h2 className="section-heading">FEATURED GEAR</h2>
          </div>
          <Link to="/shop?sort=featured">VIEW ALL <FiArrowRight /></Link>
        </div>
        <div className="home-products__rail">
          {featured.map((product) => <ProductCard key={product.id} product={product} />)}
        </div>
      </section>

      <nav className="category-tiles" aria-label="Shop by category">
        {categoryTiles.map((tile) => (
          <Link key={tile.label} to={`/shop?category=${tile.query}`}>
            <img src={tile.image} alt="" loading="lazy" />
            <span>{tile.label}</span>
            <FiArrowRight />
          </Link>
        ))}
      </nav>

      <section className="studio-story">
        <picture>
          <source media="(max-width: 680px)" srcSet="/img/story-studio-mobile.webp" />
          <img src="/img/story-studio.webp" alt="Creative gaming workspace with white and blue keyboard" loading="lazy" />
        </picture>
        <div className="studio-story__content">
          <p className="eyebrow">DESIGNED IN HONG KONG</p>
          <h2 className="display-heading">QUIET FOCUS.<br />VIVID PLAY.</h2>
          <p>{siteConfig.description}</p>
          <Link className="site-button site-button--light" to="/about">OUR STORY</Link>
        </div>
      </section>

      <section className="home-products home-products--best">
        <div className="home-section-head">
          <div>
            <p className="eyebrow">COMMUNITY FAVORITES</p>
            <h2 className="section-heading">BEST SELLERS</h2>
          </div>
          <Link to="/shop">SHOP ALL <FiArrowRight /></Link>
        </div>
        <div className="home-products__rail">
          {bestSellers.map((product) => <ProductCard key={product.id} product={product} />)}
        </div>
      </section>

      <section className="testimonials">
        {testimonials.map((item, index) => (
          <article key={item.title}>
            <span>0{index + 1}</span>
            <div className="testimonials__stars" aria-label="5 stars">★★★★★</div>
            <h3>{item.title}</h3>
            <blockquote>“{item.quote}”</blockquote>
            <cite>{item.author}</cite>
          </article>
        ))}
      </section>

      <section className="quality-split">
        <div className="quality-split__image">
          <img src="/img/product-11-detail.webp" alt="Close view of translucent mechanical keyboard" loading="lazy" />
        </div>
        <div className="quality-split__copy">
          <p className="eyebrow">MATERIAL MATTERS</p>
          <h2 className="section-heading">UNMATCHED DESIGN<br />+ QUALITY</h2>
          <p>
            Each layout is considered as a complete object—from the weight and finish of the case to the tone of every keystroke. Durable PBT keycaps keep their color and texture through long sessions.
          </p>
          <ul>
            <li><span>01</span> HOT-SWAP SWITCH FLEXIBILITY</li>
            <li><span>02</span> MULTILAYER ACOUSTIC DAMPENING</li>
            <li><span>03</span> ORIGINAL GRAPHIC KEYCAP DESIGN</li>
          </ul>
          <Link className="site-button site-button--dark" to="/shop?category=Keyboards">EXPLORE ALL KEYBOARDS</Link>
        </div>
      </section>

      <section className="home-final-cta">
        <p>YOUR DESK IS A PLACE YOU RETURN TO.</p>
        <h2>MAKE IT YOURS.</h2>
        <Link className="site-button site-button--dark" to="/shop">BUILD YOUR SETUP <FiArrowRight /></Link>
      </section>
    </main>
  );
}

export default Main;
